import React, { useMemo, useState } from 'react';
import { calculateItemTotal } from '../../domain/calculations';

/**
 * EstimatItemRowCustomer - Renderar en enskild item-rad i estimat-tabellen för KUND-vy
 * Skrivskyddad med möjlighet att kommentera
 *
 * 🚀 OPTIMERING: Använder useMemo för att cacha beräkningar (denna komponent renderas många gånger)
 *
 * @param {string} displayName - Visningsnamn för item
 * @param {Object} item - Item-data med price, quantity, selected
 * @param {string} categoryKey - Kategori-nyckel (t.ex. 'byggoffert')
 * @param {string} itemName - Item-namn
 * @param {string} commentKey - Nyckel för att identifiera kommentaren (categoryKey_itemName)
 * @param {Object} comments - Kundens kommentarer
 * @param {Object} commentsRaw - Raw kommentar-data från backend
 * @param {Object} estimatData - Estimat-data med byggkonsultResponses
 * @param {Object} showComments - State för vilka kommentarer som visas
 * @param {Function} toggleCommentField - Funktion för att toggla kommentarsfält (categoryKey, itemName)
 * @param {Function} handleCommentChange - Funktion för att ändra kommentar (categoryKey, itemName, value)
 * @param {Function} handleSaveComment - Funktion för att spara kommentar (categoryKey, itemName)
 * @param {Function} hasUnsavedChanges - Funktion för att kolla osparade ändringar (categoryKey, itemName)
 * @param {Function} isSavedLocally - Funktion för att kolla om sparat lokalt (categoryKey, itemName)
 * @param {Function} logActivity - Funktion för att logga aktivitet
 * @param {boolean} isSubcategoryItem - Om detta är en subcategory-item (indenterad)
 * @param {number} adjustedPrice - Justerat pris inkl proportionell baspaket-andel (för kundvisning)
 */
const EstimatItemRowCustomer = ({
  displayName,
  item,
  categoryKey,
  itemName,
  commentKey,
  comments,
  commentsRaw,
  estimatData,
  showComments,
  toggleCommentField,
  handleCommentChange,
  handleSaveComment,
  handleDeleteComment,
  handleSaveByggkonsultComment,
  handleEditByggkonsultEntry,
  isCustomerView = true,
  hasUnsavedChanges,
  isSavedLocally,
  logActivity,
  isSubcategoryItem = false,
  adjustedPrice, // Nytt prop: pris inkl baspaket-fördelning
}) => {
  // 🚀 OPTIMERING: Cacha total och isPreliminary
  // Om adjustedPrice finns (från EstimatCategoryTable), använd det
  // Annars fallback till calculateItemTotal
  const total = useMemo(() => {
    if (adjustedPrice !== undefined && adjustedPrice !== null) {
      return adjustedPrice;
    }
    return calculateItemTotal(item);
  }, [item, adjustedPrice]);
  const isPreliminary = useMemo(() => item?.selected && total === 0, [item?.selected, total]);

  // 🚀 OPTIMERING: Cacha kommentar-info
  const commentAuthor = useMemo(
    () => typeof commentsRaw[commentKey] === 'object' ? commentsRaw[commentKey]?.author?.name : null,
    [commentsRaw, commentKey]
  );
  
  // 🚀 OPTIMERING: Cacha kommentar-status
  const { hasCustomerComment, hasConsultantReply, hasAnyContent } = useMemo(() => {
    const rawComment = commentsRaw[commentKey];
    const customerComment = Boolean(rawComment);
    const customerDeleted = rawComment && typeof rawComment === 'object' && rawComment.deleted;
    const consultantObj = estimatData?.byggkonsultResponses?.[commentKey];
    const consultantReply = Boolean(consultantObj);
    // BK:s svar räknas som "publicerat" bara om parent.draft inte är true.
    // Endast-drafts ska INTE trigga tråd-vyn — då växlar UI till skrivläge
    // så BK kan editera sin text.
    const consultantIsDraftOnly = consultantObj && typeof consultantObj === 'object' && consultantObj.draft === true;
    const consultantHasPublished = consultantReply && !consultantIsDraftOnly;

    // För kund-vy: visa om kunden har kommenterat (inkl. borttagna om BK svarat)
    // Men visa INTE proaktiva BK-anteckningar (där kund aldrig kommenterat)
    // För BK-vy: visa tråd om publicerad konversation finns; annars (bara draft) skrivläge
    const anyContent = isCustomerView
      ? customerComment
      : (customerComment || consultantHasPublished);

    return {
      hasCustomerComment: customerComment,
      hasConsultantReply: consultantReply,
      isCustomerDeleted: Boolean(customerDeleted),
      hasAnyContent: anyContent
    };
  }, [commentsRaw, commentKey, estimatData?.byggkonsultResponses, isCustomerView]);

  // Bestäm knapp-text baserat på state och användartyp
  const getButtonText = () => {
    if (showComments[commentKey]) return 'Dölj';

    // För byggkonsult: skilja på "Visa anteckning" (egen) vs "Visa kommentar" (kund)
    if (!isCustomerView) {
      // Byggkonsult-vy
      if (hasCustomerComment) {
        return 'Visa kommentar'; // Kundens kommentar finns
      }
      if (hasConsultantReply) {
        return 'Visa anteckning'; // Endast byggkonsultens egen anteckning
      }
      // Inget finns ännu
      const commentValue = comments[commentKey];
      const commentText = typeof commentValue === 'object'
        ? (commentValue?.text || '')
        : (commentValue || '');
      return commentText.trim() ? 'Visa anteckning' : 'Anteckna';
    }

    // För kund-vy: Visa ENDAST om kunden själv har kommenterat
    // Proaktiva byggkonsult-anteckningar (utan kundkommentar) är interna och ska inte visas
    if (!hasCustomerComment) {
      // Ingen kundkommentar finns - visa bara "Kommentera" (oavsett om byggkonsult har intern anteckning)
      const commentValue = comments[commentKey];
      const commentText = typeof commentValue === 'object'
        ? (commentValue?.text || '')
        : (commentValue || '');
      return commentText.trim() ? 'Visa kommentar' : 'Kommentera';
    }

    // Kundkommentar finns - kolla om det finns svar
    if (hasConsultantReply) return 'Visa svar';
    return 'Visa kommentar';
  };

  // Kontrollera om det finns osparad kommentar (rosa prick)
  const hasUnsavedComment = (() => {
    const commentValue = comments[commentKey];
    const commentText = typeof commentValue === 'object' 
      ? (commentValue?.text || '') 
      : (commentValue || '');
    return commentText.trim();
  })();

  return (
    <>
      <tr className="border-t border-gray-200 hover:bg-gray-50">
        {/* Arbetsmoment */}
        <td className={`px-2 sm:px-4 py-1.5 sm:py-2 text-sm text-gray-700 ${isSubcategoryItem ? 'pl-4 sm:pl-6' : ''}`}>
          {displayName}
        </td>

        {/* Summa */}
        <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-right text-sm text-gray-700">
          {isPreliminary ? (
            <span className="italic font-normal text-gray-600">preliminärt</span>
          ) : (
            `${total.toLocaleString('sv-SE')} kr`
          )}
        </td>

        {/* Kommentar */}
        <td className="p-0 sm:px-4 sm:py-2 text-center w-0 max-w-0 sm:max-w-none sm:w-auto overflow-hidden">
          <div className="hidden sm:flex items-center justify-center gap-1">
            <span className="relative inline-block">
              <button
                onClick={() => toggleCommentField(categoryKey, itemName)}
                className="text-blue-600 hover:text-blue-800 text-sm underline peer"
                aria-label={commentAuthor ? `Kommentar från ${commentAuthor}` : 'Kommentera'}
              >
                {getButtonText()}
              </button>
              
              {/* Tooltip med författarnamn */}
              {commentAuthor && (
                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 peer-hover:opacity-100 transition-opacity">
                  Kommentar från {commentAuthor}
                </span>
              )}
            </span>
            
            {/* Rosa prick för osparade kommentarer */}
            {hasUnsavedComment && (
              <div 
                className="w-2 h-2 bg-leadme-pink rounded-full flex-shrink-0 animate-pulse" 
                title="Har kommentar"
              />
            )}
          </div>
        </td>
      </tr>

      {/* Kommentarsfält (expanderat) */}
      {showComments[commentKey] && (
        <tr className="bg-blue-50">
          <td colSpan="3" className="px-2 sm:px-4 py-2 sm:py-3">
            {hasAnyContent ? (
              // LÄSLÄGE: Visa konversationstråd
              <CommentThreadView
                displayName={displayName}
                categoryKey={categoryKey}
                itemName={itemName}
                commentKey={commentKey}
                comments={comments}
                commentsRaw={commentsRaw}
                estimatData={estimatData}
                isCustomerView={isCustomerView}
                handleCommentChange={handleCommentChange}
                handleSaveComment={handleSaveComment}
                handleDeleteComment={handleDeleteComment}
                handleEditByggkonsultEntry={handleEditByggkonsultEntry}
              />
            ) : (
              // SKRIVLÄGE: Visa kommentarsfält
              <CommentWriteForm
                displayName={displayName}
                categoryKey={categoryKey}
                itemName={itemName}
                commentKey={commentKey}
                comments={comments}
                handleCommentChange={handleCommentChange}
                handleSaveComment={handleSaveComment}
                handleDeleteComment={handleDeleteComment}
                handleSaveByggkonsultComment={handleSaveByggkonsultComment}
                isCustomerView={isCustomerView}
                hasUnsavedChanges={hasUnsavedChanges}
                isSavedLocally={isSavedLocally}
                logActivity={logActivity}
                estimatData={estimatData}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * CommentThreadView - Visar konversationstråd mellan kund och byggkonsult
 * Varje part kan bara redigera/ta bort sina egna meddelanden
 */
const CommentThreadView = ({ displayName, categoryKey, itemName, commentKey, comments, commentsRaw, estimatData, isCustomerView, handleCommentChange, handleSaveComment, handleDeleteComment, handleEditByggkonsultEntry }) => {
  const [editing, setEditing] = useState(false);
  const [editingEntryIdx, setEditingEntryIdx] = useState(null);
  const [editingEntryText, setEditingEntryText] = useState('');
  const [savingEntry, setSavingEntry] = useState(false);
  // Bygg kompletta tråden från både kund och konsult
  const customerComment = commentsRaw[commentKey];
  const consultantComment = estimatData?.byggkonsultResponses?.[commentKey];

  const isCustomerDeleted = customerComment && typeof customerComment === 'object' && customerComment.deleted;

  const customerText = typeof customerComment === 'object'
    ? (customerComment?.text || '')
    : (customerComment || '');
  const customerAt = (typeof customerComment === 'object' && (customerComment?.commentedAt || customerComment?.at))
    ? (customerComment.commentedAt || customerComment.at)
    : null;
  const customerName = (typeof customerComment === 'object' && customerComment?.author?.name)
    ? customerComment.author.name
    : 'Du';

  const consultantText = consultantComment
    ? (typeof consultantComment === 'object' ? (consultantComment?.text || '') : (consultantComment || ''))
    : '';
  const consultantAt = (consultantComment && typeof consultantComment === 'object' && (consultantComment?.respondedAt || consultantComment?.at))
    ? (consultantComment.respondedAt || consultantComment.at)
    : null;
  const consultantName = (consultantComment && typeof consultantComment === 'object' && consultantComment?.author?.name)
    ? consultantComment.author.name
    : 'Byggkonsult';

  // Hämta trådar
  const customerThread = Array.isArray(customerComment?.thread) ? customerComment.thread : [];
  const consultantThread = Array.isArray(consultantComment?.thread) ? consultantComment.thread : [];

  // Bygg meddelande-array (exkludera borttagna kundkommentarer)
  const customerBase = (customerText && !isCustomerDeleted)
    ? [{ by: 'customer', text: customerText, at: customerAt, author: { name: customerName } }]
    : [];
  const consultantBase = consultantText ? [{ by: 'consultant', text: consultantText, at: consultantAt, author: { name: consultantName } }] : [];

  let messages = [];
  if (customerThread.length > 0 || consultantThread.length > 0) {
    // Filtrera bort borttagna kundmeddelanden från tråden
    const filteredCustomerThread = isCustomerDeleted ? [] : customerThread;
    // Tagga consultant-entries med _consultantIndex så vi kan referera tillbaka
    // till backend's byggkonsultResponses[key].thread vid edit
    const consultantTagged = consultantThread.map((e, i) => ({ ...e, _consultantIndex: i }));
    messages = [...filteredCustomerThread, ...consultantTagged];
  } else {
    messages = [...customerBase, ...consultantBase];
  }

  messages = messages.filter(Boolean).sort((a, b) => {
    const ta = a?.at ? new Date(a.at).getTime() : 0;
    const tb = b?.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });

  // Dedupe på by + text (DB kan innehålla samma meddelande flera gånger med olika tidsstämplar)
  {
    const seen = new Set();
    messages = messages.filter(msg => {
      const key = `${msg.by}::${(msg.text || '').trim()}`;
      if (!msg.text || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  if (messages.length === 0) {
    return <div className="text-sm text-gray-500 italic">Ingen kommentar</div>;
  }

  // Kund kan bara redigera/ta bort sin egen kommentar (inte om den redan är borttagen)
  const canEditOwnComment = isCustomerView && !isCustomerDeleted && customerText;
  const canDeleteOwnComment = isCustomerView && !isCustomerDeleted && customerText;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">Konversation om: {displayName}</h4>
        {(canEditOwnComment || canDeleteOwnComment) && (
          <div className="flex items-center gap-1">
            {canEditOwnComment && handleSaveComment && (
              <button
                onClick={() => {
                  if (!editing) {
                    handleCommentChange(categoryKey, itemName, customerText);
                  }
                  setEditing(!editing);
                }}
                className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                title={editing ? 'Avbryt redigering' : 'Redigera din kommentar'}
              >
                {editing ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                )}
              </button>
            )}
            {canDeleteOwnComment && handleDeleteComment && (
              <button
                onClick={() => handleDeleteComment(categoryKey, itemName)}
                className="p-1.5 text-red-400 hover:text-red-600 transition-colors"
                title="Ta bort din kommentar"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {isCustomerDeleted && !consultantText && (
        <div className="text-sm text-gray-400 italic">Kommentaren har tagits bort.</div>
      )}

      {editing ? (
        <textarea
          value={comments[commentKey] || ''}
          onChange={(e) => handleCommentChange(categoryKey, itemName, e.target.value)}
          onBlur={() => {
            handleSaveComment(categoryKey, itemName);
            setEditing(false);
          }}
          autoFocus
          className="w-full p-2 border border-gray-300 rounded-md text-sm"
          rows="3"
        />
      ) : (
        <div className="mt-1 space-y-3">
          {messages.map((message, index) => {
            const canEditEntry = !isCustomerView && message.by === 'consultant'
              && typeof message._consultantIndex === 'number' && handleEditByggkonsultEntry
              && !message.draft;
            const isEditingThis = editingEntryIdx === index;

            return (
            <div key={index}>
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <span className="font-medium">
                  {message?.author?.name || (message.by === 'consultant' ? 'Byggkonsult' : 'Du')}:
                </span>
                {message.at && (
                  <span>
                    {new Date(message.at).toLocaleString('sv-SE', {
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })}
                  </span>
                )}
                {message.editedAt && (
                  <span className="italic text-gray-400" title={`Redigerad ${new Date(message.editedAt).toLocaleString('sv-SE')}`}>
                    (redigerad)
                  </span>
                )}
                {canEditEntry && !isEditingThis && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingEntryIdx(index);
                      setEditingEntryText(message.text || '');
                    }}
                    className="ml-auto text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    title="Redigera detta svar"
                  >
                    ✏ Redigera
                  </button>
                )}
              </div>
              {isEditingThis ? (
                <div className="mt-1">
                  <textarea
                    value={editingEntryText}
                    onChange={(e) => setEditingEntryText(e.target.value)}
                    rows={3}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                    autoFocus
                  />
                  <div className="flex gap-2 mt-1.5">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!editingEntryText.trim()) return;
                        setSavingEntry(true);
                        try {
                          await handleEditByggkonsultEntry(commentKey, message._consultantIndex, editingEntryText.trim());
                          setEditingEntryIdx(null);
                          setEditingEntryText('');
                        } finally {
                          setSavingEntry(false);
                        }
                      }}
                      disabled={savingEntry || !editingEntryText.trim()}
                      className="text-xs px-3 py-1 bg-leadme-pink text-white rounded hover:bg-leadme-pink-dark disabled:opacity-50"
                    >
                      {savingEntry ? 'Sparar…' : 'Spara'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditingEntryIdx(null); setEditingEntryText(''); }}
                      disabled={savingEntry}
                      className="text-xs px-3 py-1 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      Avbryt
                    </button>
                  </div>
                </div>
              ) : (
                <div className={`text-sm mt-1 ${message.by === 'consultant' ? 'text-green-700' : 'text-gray-800'}`}>
                  {message.text}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * CommentWriteForm - Formulär för att skriva ny kommentar
 */
const CommentWriteForm = ({
  displayName,
  categoryKey,
  itemName,
  commentKey,
  comments,
  handleCommentChange,
  handleSaveComment,
  handleDeleteComment,
  handleSaveByggkonsultComment,
  isCustomerView = true,
  hasUnsavedChanges,
  isSavedLocally,
  logActivity,
  estimatData,
}) => {
  const commentText = comments[commentKey] || '';
  const hasSavedComment = Boolean(estimatData?.customerResponse?.comments?.[commentKey]);

  return (
    <div className="flex flex-col gap-3">
      <label className="text-sm font-medium text-gray-700">
        {isCustomerView ? 'Din kommentar om:' : 'Anteckning om:'} {displayName}
      </label>

      <textarea
        value={commentText}
        onChange={(e) => handleCommentChange(categoryKey, itemName, e.target.value)}
        onBlur={() => {
          if (isCustomerView) {
            handleSaveComment(categoryKey, itemName);
          } else {
            handleSaveByggkonsultComment(categoryKey, itemName);
          }
        }}
        placeholder={isCustomerView
          ? "Skriv din kommentar eller fråga om denna punkt..."
          : "Skriv en anteckning om denna punkt (t.ex. för omräkning)..."
        }
        className="w-full p-2 border border-gray-300 rounded-md text-sm"
        rows="3"
      />

      {/* Knappar */}
      <div className="flex items-center gap-2 mt-2">
        {hasSavedComment && isCustomerView && (
          <button
            onClick={() => handleDeleteComment(categoryKey, itemName)}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded"
          >
            Ta bort
          </button>
        )}
      </div>
    </div>
  );
};

export default EstimatItemRowCustomer;

